import { useState } from "react";
import { ChevronRight, Copy, Plus, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { hasPermission, useClaims } from "@/hooks/use-claims";
import { useTranslation } from "@/i18n/use-translation";
import { useAdminOrgStore } from "@/stores/admin-org";

import { COURSES, COURSE_LABELS, courseOf, type Course } from "./course";
import { BackLink, CostCell, LoadError, PickASite, RowsSkeleton, useOpen } from "./menu-parts";
import {
  costWarning,
  isReading,
  menuLabel,
  menuRank,
  runsAt,
  type Dish,
  type Menu,
} from "./menu-model";
import { useSiteCurrency, useSiteDishes, useSiteMenus, useSiteToday } from "./use-menus";

/**
 * All recipes (POS2-1456): every dish on one site's menus, once each, with the menus it is on.
 * Filters by name, by menu and by course. Replaces the recipes list.
 */

const COURSE_PILLS: (Course | "all")[] = ["all", ...COURSES];

export default function AllRecipesPage() {
  const { t } = useTranslation();
  const open = useOpen();
  const { isPlatformAdmin, permissions } = useClaims();
  const canAdd = isPlatformAdmin || hasPermission(permissions, "recipe:create");
  const siteId = useAdminOrgStore((s) => s.siteId);
  const siteName = useAdminOrgStore((s) => s.siteName);
  return (
    <div className="mx-auto max-w-5xl">
      <BackLink label={t("menus.title")} to="/menus" />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-lg font-medium text-gray-900">{t("menus.recipes.title")}</h1>
        {siteId && canAdd && (
          <Button size="sm" onClick={() => open("/menus/recipes/new", t("menus.recipes.title"))}>
            <Plus />
            {t("menus.recipe.addTitle")}
          </Button>
        )}
      </div>
      {siteId ? (
        <>
          <p className="mb-5 text-sm text-gray-500">
            {t("menus.recipes.description", { site: siteName })}
          </p>
          <SiteDishes key={siteId} siteId={siteId} />
        </>
      ) : (
        <div className="mt-6">
          <PickASite />
        </div>
      )}
    </div>
  );
}

function SiteDishes({ siteId }: { siteId: string }) {
  const { t, locale } = useTranslation();
  const open = useOpen();
  const dishesQuery = useSiteDishes(siteId);
  const menusQuery = useSiteMenus(siteId, false);
  const currency = useSiteCurrency(siteId);
  const today = useSiteToday(siteId);
  const [query, setQuery] = useState("");
  // "all", "live", or one menu's id.
  const [scope, setScope] = useState("all");
  const [course, setCourse] = useState<Course | "all">("all");

  if (dishesQuery.isPending || menusQuery.isPending || today === null) return <RowsSkeleton />;
  if (dishesQuery.isError) return <LoadError onRetry={() => dishesQuery.refetch()} />;
  if (menusQuery.isError) return <LoadError onRetry={() => menusQuery.refetch()} />;

  const label = (m: Menu) => menuLabel(t, locale, m);
  const running = (m: Menu) => runsAt(m, siteId, today);
  const rank = (m: Menu) => menuRank(m, siteId, today);
  const byId = new Map(menusQuery.data.map((m) => [m.id, m]));
  const rows = dishesQuery.data.map((dish) => ({
    dish,
    // The site's list is a separate read: a menu archived in between is simply not shown.
    on: dish.menu_ids
      .flatMap((id) => byId.get(id) ?? [])
      .sort((a, b) => rank(a) - rank(b) || label(a).localeCompare(label(b))),
  }));

  const needle = query.trim().toLowerCase();
  const visible = rows.filter(({ dish, on }) => {
    if (scope === "live" && !on.some(running)) return false;
    if (scope !== "all" && scope !== "live" && !dish.menu_ids.includes(scope)) return false;
    if (course !== "all" && courseOf(dish.category_slug) !== course) return false;
    return dish.name.toLowerCase().includes(needle);
  });
  const pickable = menusQuery.data
    .filter((m) => !isReading(m))
    .sort((a, b) => label(a).localeCompare(label(b)));
  const th = "text-xs uppercase tracking-wide text-gray-400";

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-lg border border-black/[0.06] bg-white px-6 py-14 text-center">
        <p className="text-sm font-medium text-gray-900">{t("menus.recipes.emptyTitle")}</p>
        <p className="mt-1 max-w-sm text-sm text-gray-500">{t("menus.recipes.emptyHint")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-48 flex-1">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-300" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("menus.recipes.search")}
            aria-label={t("menus.recipes.search")}
            className="h-9 pl-9"
          />
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="h-9 w-52" aria-label={t("menus.recipes.menuFilter")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("menus.recipes.allMenus")}</SelectItem>
            <SelectItem value="live">{t("menus.recipes.liveOnly")}</SelectItem>
            {pickable.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {label(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-1">
        {COURSE_PILLS.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={course === c}
            onClick={() => setCourse(c)}
            className={`rounded-md px-3 py-1 text-sm font-medium ${
              course === c ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            {c === "all" ? t("common.all") : t(COURSE_LABELS[c])}
          </button>
        ))}
        <span className="ml-auto text-sm text-gray-500">
          {t("menus.detail.recipeCount", { count: visible.length })}
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-black/[0.06] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={`pl-4 ${th}`}>{t("menus.columnRecipe")}</TableHead>
              <TableHead className={`hidden w-64 md:table-cell ${th}`}>
                {t("menus.recipes.columnOnMenus")}
              </TableHead>
              <TableHead className={`hidden w-24 sm:table-cell ${th}`}>
                {t("menus.detail.columnCourse")}
              </TableHead>
              <TableHead className={`w-28 pr-4 text-right ${th}`}>
                {t("menus.columnCostPerPortion")}
              </TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-sm text-gray-500">
                  {t("menus.recipes.noResults")}
                </TableCell>
              </TableRow>
            )}
            {visible.map(({ dish, on }) => {
              const dishCourse = courseOf(dish.category_slug);
              const courseLabel = dishCourse ? t(COURSE_LABELS[dishCourse]) : null;
              return (
                <TableRow
                  key={dish.key}
                  className="cursor-pointer hover:bg-black/[0.02]"
                  onClick={() => open(`/menus/recipes/${dish.recipe_id}`, t("menus.recipes.title"))}
                >
                  <TableCell className="whitespace-normal py-2.5 pl-4">
                    <p className="text-sm font-medium text-gray-900">{dish.name}</p>
                    <DishFlag dish={dish} liveOn={on.filter(running).length} />
                    <p className="text-xs text-gray-400 md:hidden">
                      {[courseLabel, on.map(label).join(", ")].filter(Boolean).join(" · ")}
                    </p>
                  </TableCell>
                  <TableCell className="hidden whitespace-normal md:table-cell">
                    <div className="flex flex-col gap-0.5">
                      {on.slice(0, 2).map((m) => (
                        <span
                          key={m.id}
                          className="flex items-center gap-1.5 text-xs text-gray-600"
                        >
                          <span
                            className={`size-1.5 shrink-0 rounded-full ${
                              running(m) ? "bg-emerald-500" : "bg-gray-300"
                            }`}
                          />
                          {label(m)}
                        </span>
                      ))}
                      {on.length > 2 && (
                        <span className="pl-3 text-xs text-gray-400">
                          {t("menus.recipes.more", { count: on.length - 2 })}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden text-sm text-gray-500 sm:table-cell">
                    {courseLabel ?? "-"}
                  </TableCell>
                  <TableCell className="pr-4 text-right">
                    <CostCell
                      perPortion={dish.cost_per_portion}
                      weightKg={dish.portion_weight_kg}
                      currency={currency}
                    />
                  </TableCell>
                  <TableCell className="pr-3">
                    <ChevronRight className="size-4 text-gray-300" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

/** One flag per dish: a missing or estimated cost, else on more than one live menu, else matched. */
function DishFlag({ dish, liveOn }: { dish: Dish; liveOn: number }) {
  const { t } = useTranslation();
  const warning = costWarning(t, dish);
  if (warning) {
    return <p className="text-xs text-amber-700">{warning}</p>;
  }
  if (liveOn > 1) {
    return (
      <p className="flex items-center gap-1 text-xs text-amber-700">
        <Copy className="size-3 shrink-0" />
        {t("menus.recipes.onLiveMenus", { count: liveOn })}
      </p>
    );
  }
  return <p className="text-xs text-gray-400">{t("menus.detail.matched")}</p>;
}
